import React from "react";

/**
 * Standardized data shape for table cells that contain pills (badges/chips).
 *
 * Cell value shape:
 *   {
 *     text: "primary display text",
 *     pills: [
 *       { label: "Mismatch", style: { backgroundColor: "red" }, title: "tooltip", displayText: "Alt Text", excludeFromCSV: true }
 *     ]
 *   }
 *
 * - `label` is the pill identifier used for filtering and CSV export.
 * - `displayText` overrides what is rendered visually (label is still used for CSV/filter).
 * - `style` is optional inline styles for the pill.
 * - `title` is optional tooltip text.
 * - `excludeFromCSV` hides this pill from CSV export columns.
 */

/**
 * Renders a single pill.
 */
function Pill({ label, style, title, displayText }) {
	return (
		<span className="tagPill" style={style} title={title}>
			{displayText || label}
		</span>
	);
}

/**
 * Renders a cell value with optional inline pills.
 * Accepts either:
 *   - a { text, pills } object
 *   - a plain string/number (rendered as-is with no pills)
 */
function CellWithPills({ value }) {
	if (value == null) return "";
	if (typeof value !== "object" || !("text" in value)) return value;

	const { text, pills } = value;
	return (
		<span>
			{text}
			{pills && pills.length > 0
				? pills.map((pill, i) => (
						<Pill
							key={pill.label + i}
							label={pill.label}
							style={pill.style}
							title={pill.title}
							displayText={pill.displayText}
						/>
					))
				: null}
		</span>
	);
}

// --- Helpers for column definitions ---

/**
 * Extract the text portion from a cell value (plain string or { text, pills } shape).
 */
function getCellText(value) {
	if (value == null) return "";
	if (typeof value === "object" && "text" in value) return value.text || "";
	return "" + value;
}

/**
 * Extract pill labels from a { text, pills } value for filtering.
 * Returns text + pill labels concatenated so header filters match both.
 */
function getCellFilterValue(value) {
	if (value == null) return "";
	if (typeof value === "object" && "text" in value) {
		const parts = [value.text || ""];
		if (value.pills) {
			value.pills.forEach((p) => parts.push(p.label));
		}
		return parts.join(" ");
	}
	return "" + value;
}

/**
 * Extract CSV-exportable pill labels (semicolon-separated), excluding pills with excludeFromCSV.
 */
function getCellPillsForCSV(value) {
	if (value == null || typeof value !== "object" || !value.pills) return "";
	const exportable = value.pills.filter((p) => !p.excludeFromCSV);
	if (exportable.length === 0) return "";
	return exportable.map((p) => p.label).join("; ");
}

/**
 * Build a pill-aware column definition.
 *
 * Takes a base column config and a pill config, and returns:
 *   - the modified column (Cell renders CellWithPills, filterValueMethod set)
 *   - an extra CSV column for the pills (or null if no pills should export)
 *
 * @param {Object} columnDef - base column definition ({ Header, accessor, ... })
 * @param {Object} opts
 * @param {Function} [opts.renderCell] - optional wrapper around the CellWithPills output, receives (cellWithPillsElement, row)
 * @returns {Object} modified column definition
 */
function withPills(columnDef, opts = {}) {
	const { renderCell } = opts;

	return {
		...columnDef,
		Cell: (row) => {
			const element = <CellWithPills value={row.value} />;
			return renderCell ? renderCell(element, row) : element;
		},
		filterValueMethod: (row) => {
			const accessor = columnDef.accessor;
			const value =
				typeof accessor === "function" ? accessor(row) : row[accessor];
			return getCellFilterValue(value);
		},
	};
}

/**
 * Helper to build a { text, pills } cell value from a primary text and conditional pills.
 *
 * @param {string} text - the main text value
 * @param {Array} conditions - array of [conditionFn, pillLabel] pairs
 * @param {Object} pillConfig - map of pill label -> { style, title, displayText, excludeFromCSV }
 * @returns {{ text: string, pills: Array }}
 */
function buildCellWithPills(text, conditions, pillConfig) {
	const pills = [];
	conditions.forEach(([conditionFn, label]) => {
		if (conditionFn()) {
			const config = (pillConfig && pillConfig[label]) || {};
			pills.push({ label, ...config });
		}
	});
	return { text: text || "", pills };
}

export {
	CellWithPills,
	Pill,
	withPills,
	getCellText,
	getCellFilterValue,
	getCellPillsForCSV,
	buildCellWithPills,
};
